import { useEffect, useState } from 'react';
import {
  IonPage, IonHeader, IonToolbar, IonTitle, IonContent, IonButtons, IonMenuButton,
  IonCard, IonCardHeader, IonCardTitle, IonCardSubtitle, IonCardContent,
  IonButton, IonSegment, IonSegmentButton, IonLabel, IonIcon, IonInput, IonItem, IonList,
  IonSpinner, IonBadge,
} from '@ionic/react';
import {
  cardOutline, phonePortraitOutline, giftOutline, copyOutline,
  peopleOutline, cashOutline, checkmarkCircleOutline, informationCircleOutline,
} from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import {
  PAYMENT_METHODS, PAYMENT_METHOD_LABELS, formatCredits,
  type BillingPlan, type CreditEntry, type PaymentMethod, type PlanId,
} from '@ai-media/shared';
import { useBillingStore } from '@/store/billing.store';
import { toast } from '@/store/ui.store';

const METHOD_ICONS: Record<PaymentMethod, string> = {
  upi: phonePortraitOutline,
  card: cardOutline,
};

/** Credits are promotional units, never money — no currency symbol is used. */
const creditLabels = { singular: 'credit', plural: 'credits' };
const credits = (value: number) => formatCredits(value, creditLabels);

export default function BillingPage() {
  const { t } = useTranslation();
  const summary = useBillingStore((s) => s.summary);
  const loading = useBillingStore((s) => s.loading);
  const checkingOut = useBillingStore((s) => s.checkingOut);
  const load = useBillingStore((s) => s.load);
  const checkout = useBillingStore((s) => s.checkout);
  const [method, setMethod] = useState<PaymentMethod>('upi');

  useEffect(() => {
    void load();
  }, [load]);

  const plans = summary?.plans ?? [];
  const payments = summary?.payments ?? [];

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start"><IonMenuButton /></IonButtons>
          <IonTitle>{t('billing.title')}</IonTitle>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <div className="page-shell">
          {loading && !summary ? (
            <div className="empty-state" style={{ paddingTop: '30vh' }}><IonSpinner /></div>
          ) : (
            <>
              {summary?.gateway.mock && (
                <p className="billing-note">
                  <IonIcon icon={cashOutline} /> {t('billing.mockMode')}
                </p>
              )}

              <CreditBalance balance={summary?.creditBalance ?? 0} />
              <CreditStatement entries={summary?.creditEntries ?? []} />

              <h6 className="section-title">{t('billing.choosePlan')}</h6>
              {/* UPI or card — the choice pre-selects the tab on the gateway sheet. */}
              <IonSegment value={method} onIonChange={(e) => setMethod(e.detail.value as PaymentMethod)}>
                {PAYMENT_METHODS.map((value) => (
                  <IonSegmentButton key={value} value={value}>
                    <IonIcon icon={METHOD_ICONS[value]} />
                    <IonLabel>{PAYMENT_METHOD_LABELS[value]}</IonLabel>
                  </IonSegmentButton>
                ))}
              </IonSegment>
              <PlanCards method={method} plans={plans} checkingOut={checkingOut} checkout={checkout} />
              {summary?.subscription.status === 'active' && <CancelSubscription />}
              <ReferralPanel />

              <h6 className="section-title">{t('billing.history')}</h6>
              {payments.length === 0 ? (
                <p className="billing-empty">{t('billing.noPayments')}</p>
              ) : (
                <IonList inset>
                  {payments.map((payment) => (
                    <IonItem key={payment.id}>
                      <IonIcon icon={METHOD_ICONS[payment.method]} slot="start" />
                      <IonLabel>
                        <h3>{payment.plan} · {PAYMENT_METHOD_LABELS[payment.method]}</h3>
                        <p>
                          {new Date(payment.created_at).toLocaleDateString()}
                          {payment.credits_granted > 0 && ` · +${credits(payment.credits_granted)}`}
                        </p>
                      </IonLabel>
                      <IonBadge slot="end" color={payment.status === 'captured' ? 'success' : 'medium'}>
                        {t('billing.paid')}
                      </IonBadge>
                    </IonItem>
                  ))}
                </IonList>
              )}
            </>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
}

/** Balance banner that states plainly that credits are not cash. */
function CreditBalance({ balance }: { balance: number }) {
  const { t } = useTranslation();
  return (
    <IonCard className="billing-balance">
      <IonCardContent>
        <p className="billing-balance__value">{credits(balance)}</p>
        <p className="billing-balance__label">{t('billing.availableBalance')}</p>
        <p className="billing-balance__note">
          <IonIcon icon={informationCircleOutline} />
          {t('billing.creditsNotMoney')}
          <Link to="/legal/terms">{t('billing.readTerms')}</Link>
        </p>
      </IonCardContent>
    </IonCard>
  );
}

interface PlanCardsProps {
  method: PaymentMethod;
  plans: BillingPlan[];
  checkingOut: string | null;
  checkout: (plan: PlanId, method: PaymentMethod) => Promise<boolean>;
}

/**
 * Where the balance came from. Credits are a promotional unit, so showing the
 * ledger is what makes "here is what you have, and here is why" answerable
 * without any cash-value claim.
 */
function CreditStatement({ entries }: { entries: CreditEntry[] }) {
  const { t } = useTranslation();
  if (entries.length === 0) return null;

  return (
    <>
      <h6 className="section-title">{t('billing.creditActivity')}</h6>
      <IonList inset>
        {entries.slice(0, 8).map((entry) => (
          <IonItem key={entry.id}>
            <IonLabel>
              <h3>{t(`billing.reason.${entry.reason}`)}</h3>
              <p>{new Date(entry.created_at).toLocaleDateString()}</p>
            </IonLabel>
            <IonBadge slot="end" color={entry.amount >= 0 ? 'success' : 'medium'}>
              {entry.amount >= 0 ? `+${credits(entry.amount)}` : credits(entry.amount)}
            </IonBadge>
          </IonItem>
        ))}
      </IonList>
    </>
  );
}

function PlanCards({ method, plans, checkingOut, checkout }: PlanCardsProps) {
  const { t } = useTranslation();
  return (
    <div className="billing-plans">
      {plans.map((plan) => (
        <IonCard key={plan.id} className={plan.current ? 'billing-plan billing-plan--current' : 'billing-plan'}>
          <IonCardHeader>
            <IonCardTitle>
              {plan.label}
              {plan.popular && <IonBadge color="primary">{t('billing.popular')}</IonBadge>}
            </IonCardTitle>
            <IonCardSubtitle>{plan.tagline}</IonCardSubtitle>
          </IonCardHeader>
          <IonCardContent>
            {/* Credits are what the customer receives; the rupee figure is only
                the payment that buys them. */}
            <p className="billing-price">
              {plan.credits > 0 ? credits(plan.credits) : t('billing.free')}
            </p>
            {plan.pricePaise > 0 && (
              <p className="billing-charge">{t('billing.chargeVia', { method: PAYMENT_METHOD_LABELS[method] })}</p>
            )}
            {plan.creditsApplied > 0 && (
              <p className="billing-credit">
                {t('billing.creditApplied', { amount: credits(plan.creditsApplied) })}
              </p>
            )}
            <ul className="billing-highlights">
              {plan.highlights.map((highlight) => <li key={highlight}>{highlight}</li>)}
            </ul>
            <IonButton
              expand="block"
              disabled={plan.pricePaise === 0 || plan.current || checkingOut !== null}
              onClick={() => void checkout(plan.id, method)}
            >
              {checkingOut === plan.id ? <IonSpinner name="crescent" />
                : plan.current ? t('billing.currentPlan')
                : plan.pricePaise === 0 ? t('billing.free')
                : `${PAYMENT_METHOD_LABELS[method]} · ${t('billing.pay')}`}
            </IonButton>
          </IonCardContent>
        </IonCard>
      ))}
    </div>
  );
}

function CancelSubscription() {
  const { t } = useTranslation();
  const cancel = useBillingStore((s) => s.cancelSubscription);
  return (
    <IonButton expand="block" fill="outline" color="medium" onClick={() => void cancel()}>
      {t('billing.cancel')}
    </IonButton>
  );
}

function ReferralPanel() {
  const { t } = useTranslation();
  const referral = useBillingStore((s) => s.referral);
  const applyReferral = useBillingStore((s) => s.applyReferral);
  const [code, setCode] = useState('');

  if (!referral) return null;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(referral.shareUrl);
      toast(t('billing.linkCopied'), 'success');
    } catch {
      // Clipboard is unavailable (native shell, insecure origin); the link is
      // visible and selectable on screen, so just explain that.
      toast(t('billing.copyFailed'), 'warning');
    }
  };

  return (
    <>
      <h6 className="section-title">{t('billing.referral')}</h6>
      <IonCard>
        <IonCardContent>
          <p className="billing-referral-intro">{t('billing.referralIntro')}</p>
          <div className="billing-referral-code">
            <IonIcon icon={giftOutline} />
            <span>{referral.code}</span>
            <IonButton fill="clear" size="small" onClick={() => void copyLink()}>
              <IonIcon slot="icon-only" icon={copyOutline} />
            </IonButton>
          </div>
          <div className="billing-referral-stats">
            <span><IonIcon icon={peopleOutline} /> {t('billing.invited')}: {referral.invited}</span>
            <span><IonIcon icon={checkmarkCircleOutline} /> {t('billing.qualified')}: {referral.qualified}</span>
            <span>
              <IonIcon icon={giftOutline} /> {t('billing.rewarded')}: {credits(referral.rewardedCredits)}
            </span>
          </div>
          {referral.referredByCode ? (
            <p className="billing-referred">
              {t('billing.referredBy', { code: referral.referredByCode })}
            </p>
          ) : (
            <div className="billing-referral-apply">
              <IonItem>
                <IonInput
                  label={t('billing.enterCode')}
                  labelPlacement="stacked"
                  value={code}
                  onIonInput={(e) => setCode((e.target as HTMLIonInputElement).value as string)}
                />
              </IonItem>
              <IonButton
                expand="block"
                disabled={code.trim().length < 4}
                onClick={() => void applyReferral(code)}
              >
                {t('billing.applyCode')}
              </IonButton>
            </div>
          )}
        </IonCardContent>
      </IonCard>
    </>
  );
}